import { Component } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ModalController } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import { AuthService } from "src/app/_services/auth.service";
import { LocalStorageService } from "src/app/_services/localStorage.service";
import {
  isPlaceholderFirstName,
  isPlaceholderLastName,
  notPlaceholderNameValidator,
} from "src/app/helpers/profile-helper";

@Component({
  selector: "app-forced-profile-completion-modal",
  templateUrl: "./forced-profile-completion-modal.component.html",
  styleUrls: ["./forced-profile-completion-modal.component.scss"],
  standalone: false,
})
export class ForcedProfileCompletionModalComponent {
  submitted = false;
  loading = false;
  serverError = "";
  emailError = false;
  emailErrorMessage = "";
  form: FormGroup;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private localStorageService: LocalStorageService,
    private modalController: ModalController,
    private translate: TranslateService
  ) {
    const currentUser = this.localStorageService.getUserData()?.client;
    const firstName = isPlaceholderFirstName(currentUser?.first_name)
      ? ""
      : String(currentUser?.first_name ?? "").trim();
    const lastName = isPlaceholderLastName(
      currentUser?.first_name,
      currentUser?.last_name
    )
      ? ""
      : String(currentUser?.last_name ?? "").trim();
    const email = String(currentUser?.email ?? "").trim();

    this.form = this.fb.group({
      first_name: [
        firstName,
        [Validators.required, Validators.maxLength(80), notPlaceholderNameValidator()],
      ],
      last_name: [
        lastName,
        [Validators.required, Validators.maxLength(80), notPlaceholderNameValidator()],
      ],
      email: [email, [Validators.required, Validators.email]],
    });
  }

  get f() {
    return this.form.controls;
  }

  resetEmailError(): void {
    if (this.submitted) {
      this.emailError = false;
      this.emailErrorMessage = "";
    }
  }

  submit(): void {
    this.submitted = true;
    this.serverError = "";
    this.emailError = false;
    this.emailErrorMessage = "";

    if (!this.form.valid) {
      return;
    }

    const currentUser = this.localStorageService.getUserData()?.client;
    if (!currentUser) {
      this.serverError = this.translate.instant("MODALS.PROFILE_COMPLETION.ERROR_UPDATE");
      return;
    }

    const first_name = String(this.form.value.first_name ?? "").trim();
    const last_name = String(this.form.value.last_name ?? "").trim();
    const email = String(this.form.value.email ?? "").trim();

    const payload = {
      first_name,
      last_name,
      email,
      gender: currentUser.gender,
      password: "",
      phone: currentUser.phone || "",
    };

    this.loading = true;
    this.authService.updateUser(payload).subscribe({
      next: (res: any) => {
        this.loading = false;
        const updatedUser = res?.body?.data?.user || res?.body?.user;
        if (updatedUser) {
          this.localStorageService.updateUser(updatedUser);
        } else {
          this.localStorageService.updateUser({
            ...currentUser,
            first_name,
            last_name,
            email,
          });
        }
        this.modalController.dismiss({ updated: true }, "updated");
      },
      error: (error: any) => {
        this.loading = false;
        const emailErr = error?.error?.errors?.email;
        if (emailErr) {
          this.emailError = true;
          this.emailErrorMessage = this.translate.instant("EMAIL_EXIST.EMAIL_EXIST");
          return;
        }
        this.serverError = this.translate.instant("MODALS.PROFILE_COMPLETION.ERROR_UPDATE");
      },
    });
  }

  showError(controlName: string, errorKey: string): boolean {
    const control = this.form.get(controlName);
    return (
      !!control &&
      (control.touched || this.submitted) &&
      control.hasError(errorKey)
    );
  }
}
